import { addDecimals, compareDecimalStrings, subtractDecimals } from "@/shared/utils/decimal";

export type PaymentTaxBase = "COMMISSION" | "GROSS_AMOUNT";
export type PaymentTaxBearer = "CLIENT" | "COLOCA";

/** A tax the client pays, as the payments API lists it (`taxes[]`). Already inside `fee_total`. */
export interface PaymentTaxLine {
  code: string;
  name: string;
  rate: string;
  base: PaymentTaxBase;
  amount: string;
}

/** An entry of `fee_breakdown.taxes` on a payin or payout; Coloca-borne ones only reach staff. */
export interface FeeBreakdownTaxSnapshot {
  tax_config_id: string;
  tax_config_version: number;
  code: string;
  name: string;
  country: string;
  base: PaymentTaxBase;
  bearer: PaymentTaxBearer;
  rate: string;
  base_amount: string;
  total: string;
  currency: string;
}

export interface SplitFee {
  /** `fee_total` without the client's taxes: Coloca's commission (and a passed-through provider cost). */
  commission: string;
  taxes: PaymentTaxLine[];
}

/** `fee_total` includes the client's taxes; split it so a breakdown can show each one on its own line. */
export function splitFeeTotal(feeTotal: string, taxes?: PaymentTaxLine[] | null): SplitFee {
  const charged = (taxes ?? []).filter((tax) => compareDecimalStrings(tax.amount, "0") > 0);
  const taxTotal = charged.reduce((sum, tax) => addDecimals(sum, tax.amount), "0");
  return { commission: subtractDecimals(feeTotal, taxTotal), taxes: charged };
}

/** The client-borne taxes of an operation's snapshot, in the shape the API lists them. */
export function taxLinesFromBreakdown(taxes?: FeeBreakdownTaxSnapshot[] | null): PaymentTaxLine[] {
  return (taxes ?? [])
    .filter((tax) => tax.bearer === "CLIENT")
    .map((tax) => ({ code: tax.code, name: tax.name, rate: tax.rate, base: tax.base, amount: tax.total }));
}

/** "IVA 19 %" in the reader's locale — the name is operator data, the rate a percentage. */
export function formatTaxLabel(tax: Pick<PaymentTaxLine, "name" | "rate">, locale: string): string {
  const rate = new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 4 }).format(
    Number(tax.rate) / 100,
  );
  return `${tax.name} ${rate}`;
}

/**
 * The 4x1000 (GMF, Colombia's tax on financial movements) never reaches an operation screen by its name or
 * its rate: it reads as a transaction cost. Recognised by its code — the stable key that groups the tax's
 * versions — or, should an operator set the code differently, by the name.
 */
export function isTransactionFeeTax(tax: Pick<PaymentTaxLine, "code" | "name">): boolean {
  return tax.code.trim().toUpperCase() === "GMF" || /4\s*[x×]\s*1\.?000/i.test(tax.name);
}
